import { useState } from 'react';

interface TimeInputProps {
  value: number;  // 秒
  onChange: (value: number) => void;
  disabled?: boolean;
  className?: string;
}

/**
 * 時間碼輸入元件
 * 格式：HH:MM:SS.mmm
 */
export function TimeInput({ value, onChange, disabled, className }: TimeInputProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [inputValue, setInputValue] = useState('');

  // 秒數轉時間字串
  const formatTime = (seconds: number): string => {
    const totalMs = Math.max(0, Math.round(seconds * 1000));
    const h = Math.floor(totalMs / 3600000);
    const m = Math.floor((totalMs % 3600000) / 60000);
    const s = Math.floor((totalMs % 60000) / 1000);
    const ms = totalMs % 1000;
    return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}.${ms.toString().padStart(3, '0')}`;
  };

  // 時間字串轉秒數
  const parseTime = (str: string): number | null => {
    const match = str.match(/^(\d{1,2}):(\d{2}):(\d{2})\.?(\d{0,3})?$/);
    if (!match) return null;
    const [, h, m, s, ms = '0'] = match;
    return parseInt(h) * 3600 + parseInt(m) * 60 + parseInt(s) + parseInt(ms.padEnd(3, '0')) / 1000;
  };

  const handleFocus = () => {
    setIsEditing(true);
    setInputValue(formatTime(value));
  };

  const handleBlur = () => {
    setIsEditing(false);
    const parsed = parseTime(inputValue);
    if (parsed !== null && parsed >= 0) {
      onChange(parsed);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.currentTarget.blur();
    } else if (e.key === 'Escape') {
      setInputValue(formatTime(value));
      e.currentTarget.blur();
    }
  };

  return (
    <input
      type="text"
      value={isEditing ? inputValue : formatTime(value)}
      onChange={(e) => setInputValue(e.target.value)}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      disabled={disabled}
      className={`
        w-28 px-2 py-1 text-sm font-mono
        border-2 border-black rounded-lg bg-white
        focus:outline-none focus:ring-2 focus:ring-[#FF6B9D] focus:ring-offset-1
        disabled:bg-gray-100 disabled:text-gray-500
        ${className}
      `}
      placeholder="00:00:00.000"
    />
  );
}
